// Book-QR reader-discount client.
//
// Sends the signed book token (captured from the printed QR's ?bookToken=
// param) to the backend, which verifies the signature, enforces
// one-redemption-per-account + a global cap, and marks the user eligible for
// the reader discount (users/{uid}.bookDiscount). The dashboard billing flow
// then applies the discounted price at checkout.
//
// Backend contract (Firebase Functions, Python HTTP):
//   POST /v1/book/redeem
//     headers: Authorization: Bearer <Firebase ID token>
//     body: { token, attested }
//     resp: { status: "ok", status: "granted" | "already_redeemed", campaign }
//        | { status: "error", error: { code, message } }

import { auth } from "../firebase";
import { resolveApiBaseUrl } from "./apiBase";

// Matches the signed token shape: the99book.<payload>.<sig>
const TOKEN_RE = /the99book\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/;

/**
 * Pull the signed book token out of a scanned QR payload. The printed QR
 * encodes a URL (…/?bookToken=<token>), but tolerate a bare token too.
 * Returns the token string or null.
 */
export function extractBookToken(scanned) {
  if (!scanned || typeof scanned !== "string") return null;
  const text = scanned.trim();
  try {
    const url = new URL(text);
    const param = url.searchParams.get("bookToken");
    if (param && TOKEN_RE.test(param)) return param;
  } catch {
    /* not a URL — fall through to a raw-token match */
  }
  const match = text.match(TOKEN_RE);
  return match ? match[0] : null;
}

async function getIdToken() {
  const user = auth?.currentUser;
  if (!user) throw new Error("Not signed in — cannot redeem book code");
  return user.getIdToken();
}

/**
 * Redeem a signed book token for the currently signed-in user.
 *
 * @param {string} token    the signed token from the book QR (?bookToken=)
 * @param {object} [opts]
 * @param {boolean} [opts.attested=true]  the "I purchased the book" attestation
 * @returns {Promise<{ status: string, campaign?: string }>}
 * @throws {Error} with a `.code` property on a structured backend error
 */
export async function redeemBookToken(token, { attested = true } = {}) {
  if (!token) throw new Error("Missing book token");

  const baseUrl = resolveApiBaseUrl();
  const idToken = await getIdToken();
  const res = await fetch(`${baseUrl}/v1/book/redeem`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ token, attested }),
  });

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    /* non-JSON body */
  }

  if (!res.ok || payload?.status === "error") {
    const message = payload?.error?.message || `Redeem failed (${res.status})`;
    const err = new Error(message);
    err.code = payload?.error?.code || "REDEEM_FAILED";
    throw err;
  }

  return payload || { status: "ok" };
}
